import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import _ from 'lodash'
import styled from '@emotion/styled'
import { Box } from '@mui/system'
import { Divider, LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import CircularProgressWithLabel from '../../../components/muiComponents/circularProgressWithLabel'
import { testApiSlice } from '../../../features/section/testSlice'
import ResultQuestion from './components/ResultQuestion'
import ResultNavigation from './components/ResultNavigation'
import { tone, ui } from '../../../utils/uiTokens'

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'start',
        mt: 4,
    },
    main: {
        width: '100%',
        maxWidth: '900px',
        pb: 10,
        mr: 2,
        maxHeight: '65vh',
        overflow: 'auto',
    },
    aside: {
        height: '475px',
        width: '100%',
        maxWidth: '270px',
        position: 'sticky',
        marginTop: '80px',
        top: '10px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        maxWidth: '1200px',
    },
    chipIncorrect: {
        fontWeight: 600,
        fontSize: 18,
        color: tone.bad.text,
        width: '60px',
        background: tone.bad.bg,
        border: `1px solid ${tone.bad.border}`,
    },
    chipCorrect: {
        fontWeight: 600,
        fontSize: 18,
        color: tone.ok.text,
        width: '60px',
        background: tone.ok.bg,
        border: `1px solid ${tone.ok.border}`,
    },
    subtitle: {
        fontWeight: 700,
        fontSize: '20px',
        lineHeight: '25px',
        color: ui.sub,
        mb: 2,
    },
}

const TeacherExamResultPage = () => {
    const { formatMessage } = useIntl()
    const { topicTestId } = useParams()
    const [questions, setQuestions] = useState([])
    const { data: result = null, isFetching } =
        testApiSlice.useGetTestResultAnalysisQuery(+topicTestId)

    ////add property percentage to each question
    const transformQuestionResults = () => {
        let questionsClone = _.cloneDeep(result.questionResponses.questions)

        questionsClone = questionsClone.map((question) => {
            const percentage = result.questionAnalysisDTOList.find(
                (item) => item.questionId === question.id,
            ).percentage

            return { ...question, percentage }
        })

        setQuestions(questionsClone)
    }

    useEffect(() => {
        if (result) {
            transformQuestionResults()
        }
    }, [result])

    return (
        <Wrapper>
            <Content>
                {isFetching ? (
                    <LinearProgress />
                ) : result ? (
                    <>
                        <Box sx={styles.header}>
                            <Box>
                                <Header>
                                    <Title>
                                        <H3>
                                            {formatMessage({
                                                id: 'exam_results',
                                            })}
                                        </H3>
                                    </Title>
                                </Header>

                                <Box sx={styles.subtitle}>
                                    {formatMessage({ id: 'testName' })}:{' '}
                                    {result.title}
                                </Box>

                                <Box sx={styles.subtitle}>
                                    {formatMessage({
                                        id: 'numberOfParticipants',
                                    })}
                                    : {result.participatedStudents}
                                </Box>

                                <Box sx={styles.subtitle}>
                                    {formatMessage({ id: 'answers' })}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                }}
                            >
                                <Box sx={{ mr: 12 }}>
                                    <CircularProgressWithLabel
                                        variant="determinate"
                                        value={result.percentage}
                                        color="secondary"
                                        size={100}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Divider />

                        <Box sx={styles.container}>
                            <Box sx={styles.main}>
                                {questions.map((question, index) => (
                                    <ResultQuestion
                                        key={question.id}
                                        index={index}
                                        question={question}
                                    />
                                ))}
                            </Box>

                            <ResultNavigation questions={questions} />
                        </Box>
                    </>
                ) : (
                    formatMessage({ id: 'noData' })
                )}
            </Content>
        </Wrapper>
    )
}

export default TeacherExamResultPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px 40px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
